import React, { useState, useEffect } from 'react';
import { Axios } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import { onboardingStepId, storeSettingsSubsteps } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { useToast } from '@contexts/Toast';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useInputValidation } from '@hooks/useInputValidation';
import { OnBoardSettings } from '@components/OnBoardSettings';

export const StoreSettingsTaxDetails: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const serviceTaxValidation = useInputValidation('currency', '');
  const productTaxValidation = useInputValidation('currency', '');

  // Local validation errors for zero/range
  const [serviceTaxError, setServiceTaxError] = useState<string>('');
  const [productTaxError, setProductTaxError] = useState<string>('');

  const [initialServiceTax, setInitialServiceTax] = useState<string>('');
  const [initialProductTax, setInitialProductTax] = useState<string>('');
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Validate a tax rate string: must be >0 and <=100
  const validateTaxRate = (value: string): string => {
    if (!value.trim()) {
      return 'Tax rate is required.';
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number.';
    }
    if (num <= 0) {
      return 'Tax rate must be greater than 0.';
    }
    if (num > 100) {
      return 'Tax rate cannot exceed 100.';
    }
    return ''; // no error
  };

  // Handle input change with validation
  const handleServiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    serviceTaxValidation.handleChange(e);
    const error = validateTaxRate(val);
    setServiceTaxError(error);
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    productTaxValidation.handleChange(e);
    const error = validateTaxRate(val);
    setProductTaxError(error);
  };

  // Fetch tax rates on mount
  useEffect(() => {
    const fetchTaxRates = async () => {
      try {
        setLoading(true);
        const response = await Axios.post(ApiEndpoints.FETCH_TAX_RATES);
        if (response.data.status === 'success' && response.data.data) {
          const { serviceTax, productTax } = response.data.data;
          const serviceVal = serviceTax?.toString() || '';
          const productVal = productTax?.toString() || '';
          serviceTaxValidation.setValue(serviceVal);
          productTaxValidation.setValue(productVal);
          setInitialServiceTax(serviceVal);
          setInitialProductTax(productVal);
          // Validate initial values (e.g., if 0 from backend, show error)
          setServiceTaxError(validateTaxRate(serviceVal));
          setProductTaxError(validateTaxRate(productVal));
        }
      } catch (error) {
        console.error('Error fetching tax rates:', error);
        showToastMessage('Failed to load tax rates', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchTaxRates();
  }, []);

  // Detect changes
  useEffect(() => {
    if (!loading) {
      const serviceChanged = serviceTaxValidation.value !== initialServiceTax;
      const productChanged = productTaxValidation.value !== initialProductTax;
      setHasChanges(serviceChanged || productChanged);
    }
  }, [
    serviceTaxValidation.value,
    productTaxValidation.value,
    initialServiceTax,
    initialProductTax,
    loading,
  ]);

  // Check if both fields are valid (non-zero and no validation errors)
  const isFormValid = (): boolean => {
    const serviceVal = serviceTaxValidation.value;
    const productVal = productTaxValidation.value;
    const serviceErr = validateTaxRate(serviceVal);
    const productErr = validateTaxRate(productVal);
    // Update error states
    setServiceTaxError(serviceErr);
    setProductTaxError(productErr);
    return !serviceErr && !productErr;
  };

  const handleSubmit = async (
    event: React.FormEvent,
    nextPage: boolean,
  ): Promise<void> => {
    event.preventDefault();

    // Run validation
    if (!isFormValid()) {
      showToastMessage('Please fix validation errors before saving.', 'error');
      return;
    }

    const serviceNumericRate = parseFloat(serviceTaxValidation.value);
    const productNumericRate = parseFloat(productTaxValidation.value);

    // Additional safety checks
    if (serviceNumericRate <= 0 || productNumericRate <= 0) {
      showToastMessage('Tax rates must be greater than 0.', 'error');
      return;
    }
    if (serviceNumericRate > 100 || productNumericRate > 100) {
      showToastMessage('Tax rates cannot exceed 100.', 'error');
      return;
    }

    try {
      const payload = {
        serviceTax: serviceNumericRate,
        productTax: productNumericRate,
      };

      const response = await Axios.post(ApiEndpoints.UPSERT_TAX_RATES, payload);

      if (response.data.status === 'success') {
        const butcherDetails = localStorage.getItem('butcherDetails');
        let details = butcherDetails ? JSON.parse(butcherDetails) : {};
        details = {
          ...details,
          serviceTax: serviceNumericRate,
          productTax: productNumericRate,
        };
        CustomerOrderService.setButcherDetails(details);
        showToastMessage('Tax rates saved successfully', 'success');

        updateOnboardingStep(onboardingStepId.ADDRESS);

        // Reset initial values to current to reflect saved state
        setInitialServiceTax(serviceTaxValidation.value);
        setInitialProductTax(productTaxValidation.value);
        setHasChanges(false);

        if (nextPage) {
          navigate(FrontendRoutes.ROLE_MANAGEMENT, {
            state: { fromSettings: true },
          });
        }
      } else {
        showToastMessage('Failed to save tax rates. Please try again.', 'error');
      }
    } catch (error) {
      showToastMessage('Failed to save tax rates. Please try again.', 'error');
    }
  };

  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={3}
          subNavSteps={storeSettingsSubsteps}
          fromSettings={true}
          forStoreSetting={true}
        />
        <div className="mt-4 pb-20">
          <h2 className="text-lg font-medium mb-4">
            Service and Product Tax Rates
          </h2>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Loading tax rates...</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-[#F1F8ED] rounded-lg">
                <span className="text-lg font-medium text-gray-700">
                  Service Tax Rate
                </span>
                <div className="flex flex-col items-end">
                  <input
                    type="text"
                    value={serviceTaxValidation.value}
                    onChange={handleServiceChange}
                    onKeyPress={serviceTaxValidation.handleKeyPress}
                    placeholder="Tax Rate %"
                    className={`border rounded-md p-2 w-36 text-right ${
                      serviceTaxError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {serviceTaxError && (
                    <span className="text-red-500 text-xs mt-1">
                      {serviceTaxError}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-[#F1F8ED] rounded-lg">
                <span className="text-lg font-medium text-gray-700">
                  Product Tax Rate
                </span>
                <div className="flex flex-col items-end">
                  <input
                    type="text"
                    value={productTaxValidation.value}
                    onChange={handleProductChange}
                    onKeyPress={productTaxValidation.handleKeyPress}
                    placeholder="Tax Rate %"
                    className={`border rounded-md p-2 w-36 text-right ${
                      productTaxError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {productTaxError && (
                    <span className="text-red-500 text-xs mt-1">
                      {productTaxError}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col sm:flex-row items-center gap-4 p-2">
        <button
          onClick={(e) => handleSubmit(e, false)}
          disabled={
            !hasChanges ||
            loading ||
            !!serviceTaxError ||
            !!productTaxError ||
            !serviceTaxValidation.value ||
            !productTaxValidation.value
          }
          className={`w-full sm:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${
            hasChanges &&
            !serviceTaxError &&
            !productTaxError &&
            serviceTaxValidation.value &&
            productTaxValidation.value
              ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
              : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480]'
          }`}
          type="button"
        >
          Save
        </button>

        <button
          onClick={(e) => handleSubmit(e, true)}
          disabled={
            loading ||
            !!serviceTaxError ||
            !!productTaxError ||
            !serviceTaxValidation.value ||
            !productTaxValidation.value
          }
          className="w-full sm:w-[130px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center transition-colors duration-200"
          type="button"
        >
          Next
        </button>
      </div>
    </>
  );

  return <OnBoardSettings renderContent={renderContent} />;
};